/**
 * evidenceService.ts
 * Reads cross-app collections: krAssignments + keyResults
 * to pre-populate the self-review form with real KR progress data.
 */

import {
  collection, query, where, getDocs, doc, getDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { KRProgressEntry, KPIEvidenceEntry } from '../types';
import { getRelevantKRs, type CycleContext } from './cycleUtils';

export interface KRAssignmentDoc {
  id: string;
  assignedTo: string;    // userId of the member
  assignedBy: string;    // userId of the leader
  krId: string;
  krTitle: string;
  krNumber?: string;
  cycleId?: string;
  kpiTagIds?: string[];
  targetValue?: number;
  unit?: string;
}

/** Fetch KR assignments for a user and enrich with real progress from keyResults.
 *  If cycleContext is provided, only KRs relevant to that EPR period are returned.
 */
export async function fetchKRsForReview(
  userId: string,
  cycleContext?: CycleContext,
): Promise<KRProgressEntry[]> {
  const entries: KRProgressEntry[] = [];
  const seenKrIds = new Set<string>();

  // Source 1: krAssignments (members assigned by leader)
  const assignSnap = await getDocs(
    query(collection(db, 'krAssignments'), where('assignedTo', '==', userId)),
  );

  // Source 2: keyResults where ownerId == userId (for leaders/owners)
  const krDocsSnap = await getDocs(
    query(collection(db, 'keyResults'), where('ownerId', '==', userId)),
  );

  // Build map of krId → keyResult data for O(1) enrichment
  const krDataMap = new Map<string, Record<string, unknown>>();
  for (const d of krDocsSnap.docs) {
    krDataMap.set(d.id, d.data() as Record<string, unknown>);
  }

  // Process krAssignments — enrich with keyResults progress
  for (const assignDoc of assignSnap.docs) {
    const a = assignDoc.data() as KRAssignmentDoc;
    if (seenKrIds.has(a.krId)) continue;
    seenKrIds.add(a.krId);

    const kr = krDataMap.get(a.krId);
    entries.push({
      krId: a.krId,
      krTitle: a.krTitle,
      krNumber: a.krNumber,
      selfScore: 0,
      evidence: String(kr?.sourceOfVerification ?? ''),
      progress: typeof kr?.progress === 'number' ? kr.progress : 0,
      krStatus: String(kr?.status ?? ''),
      outcome: kr?.outcome ? String(kr.outcome) : undefined,
      sourceOfVerification: kr?.sourceOfVerification ? String(kr.sourceOfVerification) : undefined,
      quarter: kr?.quarter ? String(kr.quarter) : undefined,
      quarters: Array.isArray(kr?.quarters) ? (kr.quarters as string[]) : undefined,
      quarterlyBreakdown: kr?.quarterlyBreakdown as KRProgressEntry['quarterlyBreakdown'] ?? undefined,
    });
  }

  // Process keyResults owned directly (leaders)
  for (const krDoc of krDocsSnap.docs) {
    const kr = krDoc.data();
    const krId = krDoc.id;
    if (seenKrIds.has(krId)) continue;
    seenKrIds.add(krId);

    entries.push({
      krId,
      krTitle: String(kr.title ?? ''),
      krNumber: kr.krNumber ?? kr.number ?? undefined,
      selfScore: 0,
      evidence: String(kr.sourceOfVerification ?? ''),
      progress: typeof kr.progress === 'number' ? kr.progress : 0,
      krStatus: String(kr.status ?? ''),
      outcome: kr.outcome ? String(kr.outcome) : undefined,
      sourceOfVerification: kr.sourceOfVerification ? String(kr.sourceOfVerification) : undefined,
      quarter: kr.quarter ? String(kr.quarter) : undefined,
      quarters: Array.isArray(kr.quarters) ? (kr.quarters as string[]) : undefined,
      quarterlyBreakdown: kr.quarterlyBreakdown as KRProgressEntry['quarterlyBreakdown'] ?? undefined,
    });
  }

  // Apply cycle-aware filter if a cycle context was provided
  if (cycleContext) {
    return getRelevantKRs(entries, cycleContext);
  }
  return entries;
}

export interface KanbanEvidenceGroup {
  tagId: string;
  tagName: string;
  taskCount: number;
}

/** Fetch completed Kanban tasks grouped by KPI tag for a user */
export async function fetchKanbanEvidence(userId: string): Promise<KPIEvidenceEntry[]> {
  const taskSnap = await getDocs(
    query(
      collection(db, 'kanbanTasks'),
      where('assignedTo', '==', userId),
      where('status', '==', 'done'),
    ),
  );

  if (taskSnap.empty) return [];

  // Aggregate by kpiTagId
  const tagMap = new Map<string, { count: number; tagName: string }>();

  for (const tdoc of taskSnap.docs) {
    const task = tdoc.data();
    const tagIds: string[] = task.kpiTagIds || (task.kpiTagId ? [task.kpiTagId] : []);

    for (const tagId of tagIds) {
      if (!tagId) continue;
      if (!tagMap.has(tagId)) {
        // Try to resolve tag name
        let tagName = tagId;
        try {
          const tagSnap = await getDoc(doc(db, 'kpiTagLibrary', tagId));
          if (tagSnap.exists()) tagName = tagSnap.data().name || tagId;
        } catch {
          // ignore
        }
        tagMap.set(tagId, { count: 0, tagName });
      }
      tagMap.get(tagId)!.count++;
    }
  }

  return Array.from(tagMap.entries()).map(([tagId, v]) => ({
    tagId,
    tagName: v.tagName,
    taskCount: v.count,
    selfScore: 0,
  }));
}
